import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Outlet, useLocation, useMatches } from "react-router-dom";
import PageHeader from "../header";
import {
  ContentEditorShellProvider,
  createContentEditorSidebarState,
} from "../../context/ContentEditorShellContext";
import ContentEditorSidebar from "./ContentEditorSidebar";
import apiClient from "../../tools/axiosConfig";
import { CONTENT_DRAFTED_EVENT } from "../../tools/conditionalApi";
import useErrorPopup from "../../tools/hooks/showError";
import { getCourseUnits } from "../../utils/contentEditorHelpers";
import {
  clampComposerLanguage,
  getCourseSupportedLanguages,
  normalizeLanguageKey,
  readStoredComposerLanguage,
  writeStoredComposerLanguage,
} from "../../utils/composerLanguages";
import { setCourseUnits } from "../../utils/contentEditorCourseTree";
import {
  ensureLessonBundle,
  patchLessonBundle,
  readLessonBundle,
} from "../../utils/lessonEditorBundleCache";
import { formatLoadError } from "../../utils/fileUploadHelpers";
import "../../style/programDetails.css";
import "../../style/editLesson.css";

const normalizeEntityId = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  if (!normalized || normalized.toLowerCase() === "new") return null;
  return normalized;
};

const getEntityCacheKey = (kind, id) => `${kind}:${String(id)}`;

const normalizeCourseData = (data) => setCourseUnits(data, getCourseUnits(data));

const ContentEditorShellOutletFallback = () => (
  <div className="pd-main">
    <div className="pd-content-scroll">
      <div className="pd-loading">
        <div className="pd-spinner" />
      </div>
    </div>
  </div>
);

const ContentEditorShell = () => {
  const location = useLocation();
  const matches = useMatches();
  const [showError, ErrorPopupComponent] = useErrorPopup();

  const [allPrograms, setAllPrograms] = useState([]);
  const [courseTreeCache, setCourseTreeCache] = useState({});
  const [sidebarState, setSidebarState] = useState(() => createContentEditorSidebarState());
  const [resolvedTargetContext, setResolvedTargetContext] = useState({
    courseId: null,
    unitId: null,
    targetKey: null,
  });

  const entityParentCacheRef = useRef(new Map());
  const courseTreeCacheRef = useRef({});
  const inflightTasksRef = useRef(new Map());

  const deepestTargetMatch = useMemo(
    () => [...matches].reverse().find((match) => match.handle?.contentEditorTarget),
    [matches]
  );

  const activeRouteTarget = useMemo(() => {
    const descriptor = deepestTargetMatch?.handle?.contentEditorTarget;
    if (!descriptor) return null;
    const search = new URLSearchParams(location.search);
    const entityId = normalizeEntityId(
      descriptor.param ? deepestTargetMatch?.params?.[descriptor.param] : null
    );
    return {
      kind: descriptor.kind,
      entityId,
      stepId: normalizeEntityId(deepestTargetMatch?.params?.stepId),
      draftParentId: descriptor.draftParentQuery
        ? normalizeEntityId(search.get(descriptor.draftParentQuery))
        : null,
      draftParentQuery: descriptor.draftParentQuery || null,
    };
  }, [deepestTargetMatch, location.search]);

  const activeRouteTargetKey = useMemo(() => {
    if (!activeRouteTarget) return "none";
    return [
      activeRouteTarget.kind,
      activeRouteTarget.entityId || "new",
      activeRouteTarget.stepId || "",
      activeRouteTarget.draftParentId || "",
    ].join(":");
  }, [activeRouteTarget]);

  const runInflightTask = useCallback((key, runner) => {
    if (inflightTasksRef.current.has(key)) {
      return inflightTasksRef.current.get(key);
    }
    const task = Promise.resolve()
      .then(runner)
      .finally(() => {
        if (inflightTasksRef.current.get(key) === task) {
          inflightTasksRef.current.delete(key);
        }
      });
    inflightTasksRef.current.set(key, task);
    return task;
  }, []);

  const fetchPrograms = useCallback(async () => {
    const list = await runInflightTask("programs", async () => {
      const { data } = await apiClient.get("/backoffice/v1/courses/", {
        params: { summary: true },
      });
      return Array.isArray(data) ? data : data?.courses ?? [];
    });
    setAllPrograms(list);
    return list;
  }, [runInflightTask]);

  useEffect(() => {
    fetchPrograms().catch((error) => {
      console.error(error);
      showError(formatLoadError(error, "programs list"));
    });
  }, [fetchPrograms, showError]);

  useEffect(() => {
    document.body.classList.add("new-content-editor");
    return () => {
      document.body.classList.remove("new-content-editor");
    };
  }, []);

  const patchCourseTree = useCallback((courseId, updater) => {
    const normalizedCourseId = normalizeEntityId(courseId);
    if (!normalizedCourseId) return;
    setCourseTreeCache((prev) => {
      const current = prev[normalizedCourseId] || null;
      const nextValue =
        typeof updater === "function" ? updater(current) : updater;
      if (nextValue === current || !nextValue) return prev;
      const nextCache = {
        ...prev,
        [normalizedCourseId]: nextValue,
      };
      courseTreeCacheRef.current = nextCache;
      return nextCache;
    });
  }, []);

  const fetchCourseTreeById = useCallback(
    async (courseId, { force = false } = {}) => {
      const normalizedCourseId = normalizeEntityId(courseId);
      if (!normalizedCourseId) return null;
      const cachedCourse = courseTreeCacheRef.current[normalizedCourseId];
      if (!force && cachedCourse) {
        return cachedCourse;
      }
      const nextCourse = await runInflightTask(
        force ? `course:${normalizedCourseId}:refresh` : `course:${normalizedCourseId}`,
        async () => {
          const { data } = await apiClient.get(
            `/backoffice/v1/courses/${normalizedCourseId}/`
          );
          return normalizeCourseData(data);
        }
      );
      patchCourseTree(normalizedCourseId, nextCourse);
      return nextCourse;
    },
    [patchCourseTree, runInflightTask]
  );

  useEffect(() => {
    courseTreeCacheRef.current = courseTreeCache;
  }, [courseTreeCache]);

  // Refresh cached trees and the programs list after an item is switched to draft.
  useEffect(() => {
    const reload = () => {
      Object.keys(courseTreeCacheRef.current).forEach((courseId) => {
        fetchCourseTreeById(courseId, { force: true }).catch((error) => console.error(error));
      });
      fetchPrograms().catch((error) => console.error(error));
    };
    window.addEventListener(CONTENT_DRAFTED_EVENT, reload);
    return () => window.removeEventListener(CONTENT_DRAFTED_EVENT, reload);
  }, [fetchCourseTreeById, fetchPrograms]);

  const fetchEntityParents = useCallback(
    async (kind, entityId) => {
      const normalizedEntityId = normalizeEntityId(entityId);
      if (!normalizedEntityId) return { courseId: null, unitId: null };
      const cacheKey = getEntityCacheKey(kind, normalizedEntityId);
      if (entityParentCacheRef.current.has(cacheKey)) {
        return entityParentCacheRef.current.get(cacheKey);
      }

      const payload = await runInflightTask(`parents:${cacheKey}`, async () => {
        if (kind === "unit") {
          const { data } = await apiClient.get(`/backoffice/v1/units/${normalizedEntityId}/`);
          return {
            courseId: normalizeEntityId(data?.parent_course_id ?? data?.parent_course),
            unitId: normalizeEntityId(data?.id),
          };
        }

        if (kind === "lesson") {
          const { data } = await apiClient.get(`/backoffice/v1/lessons/${normalizedEntityId}/`);
          const unitId = normalizeEntityId(data?.parent_unit_id);
          let courseId = null;
          if (unitId) {
            const unitParents = await fetchEntityParents("unit", unitId);
            courseId = unitParents.courseId;
          }
          return {
            courseId,
            unitId,
          };
        }

        if (kind === "scenario") {
          const { data } = await apiClient.get(`/backoffice/v1/scenarios/${normalizedEntityId}/`);
          const unitId = normalizeEntityId(data?.parent_unit_id);
          let courseId = null;
          if (unitId) {
            const unitParents = await fetchEntityParents("unit", unitId);
            courseId = unitParents.courseId;
          }
          return {
            courseId,
            unitId,
          };
        }

        if (kind === "infoPage") {
          const { data } = await apiClient.get(`/backoffice/v1/info-pages/${normalizedEntityId}/`);
          const unitId = normalizeEntityId(data?.parent_unit_id);
          let courseId = null;
          if (unitId) {
            const unitParents = await fetchEntityParents("unit", unitId);
            courseId = unitParents.courseId;
          }
          return {
            courseId,
            unitId,
          };
        }

        return { courseId: null, unitId: null };
      });

      entityParentCacheRef.current.set(cacheKey, payload);
      return payload;
    },
    [runInflightTask]
  );

  const resolveTargetContext = useCallback(
    async (target = activeRouteTarget) => {
      if (!target) return { courseId: null, unitId: null };

      if (target.kind === "course") {
        return {
          courseId: normalizeEntityId(target.entityId),
          unitId: null,
        };
      }

      if (target.kind === "unit") {
        if (!target.entityId) {
          return {
            courseId: normalizeEntityId(target.draftParentId),
            unitId: null,
          };
        }
        return fetchEntityParents("unit", target.entityId);
      }

      if (target.kind === "lesson") {
        if (!target.entityId) {
          const unitId = normalizeEntityId(target.draftParentId);
          if (!unitId) return { courseId: null, unitId: null };
          const unitContext = await fetchEntityParents("unit", unitId);
          return {
            ...unitContext,
            unitId,
          };
        }
        return fetchEntityParents("lesson", target.entityId);
      }

      if (target.kind === "scenario") {
        if (!target.entityId) {
          const unitId = normalizeEntityId(target.draftParentId);
          if (!unitId) return { courseId: null, unitId: null };
          const unitContext = await fetchEntityParents("unit", unitId);
          return {
            ...unitContext,
            unitId,
          };
        }
        return fetchEntityParents("scenario", target.entityId);
      }

      if (target.kind === "infoPage") {
        if (!target.entityId) {
          const unitId = normalizeEntityId(target.draftParentId);
          if (!unitId) return { courseId: null, unitId: null };
          const unitContext = await fetchEntityParents("unit", unitId);
          return {
            ...unitContext,
            unitId,
          };
        }
        return fetchEntityParents("infoPage", target.entityId);
      }

      return { courseId: null, unitId: null };
    },
    [activeRouteTarget, fetchEntityParents]
  );

  const ensureCourseTree = useCallback(
    async (target = activeRouteTarget, opts = {}) => {
      const context = await resolveTargetContext(target);
      if (!context?.courseId) return context;
      await fetchCourseTreeById(context.courseId, opts);
      return context;
    },
    [activeRouteTarget, fetchCourseTreeById, resolveTargetContext]
  );

  const refreshCourseTree = useCallback(
    async (courseIdOrTarget = activeRouteTarget, opts = {}) => {
      if (typeof courseIdOrTarget === "string" || typeof courseIdOrTarget === "number") {
        return fetchCourseTreeById(courseIdOrTarget, { ...opts, force: true });
      }
      const context = await resolveTargetContext(courseIdOrTarget);
      if (!context?.courseId) return null;
      return fetchCourseTreeById(context.courseId, { ...opts, force: true });
    },
    [activeRouteTarget, fetchCourseTreeById, resolveTargetContext]
  );

  const prefetchEditorTarget = useCallback(
    async (target) => {
      if (!target) return null;
      if (target.kind === "lesson" && target.entityId) {
        await Promise.all([ensureCourseTree(target), ensureLessonBundle(target.entityId)]);
        return readLessonBundle(target.entityId);
      }
      await ensureCourseTree(target);
      return null;
    },
    [ensureCourseTree]
  );

  useEffect(() => {
    let cancelled = false;
    if (!activeRouteTarget) {
      setResolvedTargetContext({ courseId: null, unitId: null, targetKey: activeRouteTargetKey });
      return undefined;
    }
    ensureCourseTree(activeRouteTarget)
      .then((context) => {
        if (!cancelled) {
          setResolvedTargetContext({
            courseId: context?.courseId || null,
            unitId: context?.unitId || null,
            targetKey: activeRouteTargetKey,
          });
        }
      })
      .catch((error) => {
        console.error(error);
        if (!cancelled) {
          setResolvedTargetContext({
            courseId: null,
            unitId: null,
            targetKey: activeRouteTargetKey,
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [activeRouteTargetKey, activeRouteTarget, ensureCourseTree]);

  const activeCourseId = resolvedTargetContext.courseId || null;
  const activeCourse = activeCourseId ? courseTreeCache[activeCourseId] || null : null;

  /* ── Composer display language (single selector in the sidebar drives every editor page) ── */
  const [composerLanguageChoice, setComposerLanguageChoice] = useState(() =>
    readStoredComposerLanguage(null)
  );
  useEffect(() => {
    setComposerLanguageChoice(readStoredComposerLanguage(activeCourseId));
  }, [activeCourseId]);

  const composerSupportedLanguages = useMemo(
    () => getCourseSupportedLanguages(activeCourse),
    [activeCourse]
  );
  const composerLanguage = useMemo(
    () => clampComposerLanguage(composerLanguageChoice, composerSupportedLanguages),
    [composerLanguageChoice, composerSupportedLanguages]
  );
  const setComposerLanguage = useCallback(
    (lang) => {
      const normalized = normalizeLanguageKey(lang);
      if (!normalized) return;
      setComposerLanguageChoice(normalized);
      writeStoredComposerLanguage(activeCourseId, normalized);
    },
    [activeCourseId]
  );
  const resolvedContextMatchesRoute =
    resolvedTargetContext.targetKey === activeRouteTargetKey;
  // Until the route's course loads, `composerLanguage` is English, or the language of the previous route's course.
  const composerLanguageReady = Boolean(activeCourse) && resolvedContextMatchesRoute;
  const currentRouteUnitId = resolvedContextMatchesRoute
    ? resolvedTargetContext.unitId
    : null;
  const derivedActiveUnit = useMemo(() => {
    if (!activeCourse || !currentRouteUnitId) return null;
    return (
      getCourseUnits(activeCourse).find(
        (unitItem) => String(unitItem?.id) === String(currentRouteUnitId)
      ) || null
    );
  }, [activeCourse, currentRouteUnitId]);
  const effectiveActiveUnit = useMemo(() => {
    if (derivedActiveUnit) return derivedActiveUnit;

    const sidebarUnitId = sidebarState.unit?.id;
    if (sidebarUnitId == null || !activeRouteTarget) return null;

    if (
      activeRouteTarget.kind === "unit" &&
      activeRouteTarget.entityId != null &&
      String(sidebarUnitId) === String(activeRouteTarget.entityId)
    ) {
      return sidebarState.unit;
    }

    const routeLessonId =
      activeRouteTarget.kind === "lesson" ? activeRouteTarget.entityId : null;
    const sidebarLessonMatchesRoute =
      routeLessonId != null &&
      sidebarState.lesson?.id != null &&
      String(sidebarState.lesson.id) === String(routeLessonId);
    const sidebarLessonParentUnitId = sidebarState.lesson?.parent_unit_id;

    if (
      sidebarLessonMatchesRoute &&
      sidebarLessonParentUnitId != null &&
      String(sidebarLessonParentUnitId) === String(sidebarUnitId)
    ) {
      return sidebarState.unit;
    }

    const routeScenarioId =
      activeRouteTarget.kind === "scenario" ? activeRouteTarget.entityId : null;
    const sidebarScenarioMatchesRoute =
      routeScenarioId != null &&
      sidebarState.scenario?.id != null &&
      String(sidebarState.scenario.id) === String(routeScenarioId);
    const sidebarScenarioParentUnitId = sidebarState.scenario?.parent_unit_id;

    if (
      sidebarScenarioMatchesRoute &&
      sidebarScenarioParentUnitId != null &&
      String(sidebarScenarioParentUnitId) === String(sidebarUnitId)
    ) {
      return sidebarState.unit;
    }

    const routeInfoPageId =
      activeRouteTarget.kind === "infoPage" ? activeRouteTarget.entityId : null;
    const sidebarInfoPageMatchesRoute =
      routeInfoPageId != null &&
      sidebarState.infoPage?.id != null &&
      String(sidebarState.infoPage.id) === String(routeInfoPageId);
    const sidebarInfoPageParentUnitId = sidebarState.infoPage?.parent_unit_id;

    if (
      sidebarInfoPageMatchesRoute &&
      sidebarInfoPageParentUnitId != null &&
      String(sidebarInfoPageParentUnitId) === String(sidebarUnitId)
    ) {
      return sidebarState.unit;
    }

    return null;
  }, [activeRouteTarget, derivedActiveUnit, sidebarState.infoPage, sidebarState.lesson, sidebarState.scenario, sidebarState.unit]);

  const patchSidebarState = useCallback((nextState) => {
    setSidebarState((prev) => ({
      ...createContentEditorSidebarState(prev),
      ...(nextState || {}),
    }));
  }, []);

  const replaceSidebarState = useCallback((nextState) => {
    setSidebarState(createContentEditorSidebarState(nextState));
  }, []);

  const resetSidebarState = useCallback(() => {
    setSidebarState(createContentEditorSidebarState());
  }, []);

  const optimisticUnitsOrder = useCallback(
    (newUnits) => {
      if (!activeCourseId) return;
      patchCourseTree(activeCourseId, (prevCourse) => {
        if (!prevCourse) return prevCourse;
        return setCourseUnits(prevCourse, newUnits);
      });
      if (sidebarState.unit?.id != null) {
        const nextUnit = (Array.isArray(newUnits) ? newUnits : []).find(
          (unitItem) => String(unitItem?.id) === String(sidebarState.unit?.id)
        );
        if (nextUnit) {
          patchSidebarState({ unit: { ...sidebarState.unit, ...nextUnit } });
        }
      }
    },
    [activeCourseId, patchCourseTree, patchSidebarState, sidebarState.unit]
  );

  const optimisticContentOrder = useCallback(
    (unitId, newContentItems) => {
      if (!activeCourseId) return;
      const normalizedContentItems = (Array.isArray(newContentItems) ? newContentItems : []).map(
        (item, index) => ({
          ...item,
          content_type: item.type === "info-page" ? "info_page" : item.type,
          order: index + 1,
        })
      );
      patchCourseTree(activeCourseId, (prevCourse) => {
        if (!prevCourse) return prevCourse;
        const nextUnits = getCourseUnits(prevCourse).map((unitItem) =>
          String(unitItem?.id) === String(unitId)
            ? { ...unitItem, content_items: normalizedContentItems }
            : unitItem
        );
        return setCourseUnits(prevCourse, nextUnits);
      });
      if (String(sidebarState.unit?.id) === String(unitId)) {
        patchSidebarState({
          unit: {
            ...sidebarState.unit,
            content_items: normalizedContentItems,
          },
        });
      }
    },
    [activeCourseId, patchCourseTree, patchSidebarState, sidebarState.unit]
  );

  const contextValue = useMemo(
    () => ({
      activeCourse,
      activeCourseId,
      activeUnit: effectiveActiveUnit,
      allPrograms,
      ensureCourseTree,
      refreshCourseTree,
      patchCourseTree,
      prefetchEditorTarget,
      resolveTargetContext,
      replaceSidebarState,
      resetSidebarState,
      ensureLessonBundle,
      readLessonBundle,
      patchLessonBundle,
      activeRouteTarget,
      composerLanguage,
      composerLanguageReady,
      composerSupportedLanguages,
      setComposerLanguage,
    }),
    [
      activeCourse,
      activeCourseId,
      effectiveActiveUnit,
      allPrograms,
      ensureCourseTree,
      refreshCourseTree,
      patchCourseTree,
      prefetchEditorTarget,
      resolveTargetContext,
      replaceSidebarState,
      resetSidebarState,
      activeRouteTarget,
      composerLanguage,
      composerLanguageReady,
      composerSupportedLanguages,
      setComposerLanguage,
    ]
  );

  return (
    <ContentEditorShellProvider value={contextValue}>
      <div className="pd-page">
        <PageHeader headerPreviewMode="user-home" />
        <div className="pd-body">
          <ContentEditorSidebar
            course={activeCourse}
            unit={effectiveActiveUnit}
            lesson={sidebarState.lesson}
            scenario={sidebarState.scenario}
            infoPage={sidebarState.infoPage}
            lessonVersions={sidebarState.lessonVersions}
            activeVersionId={sidebarState.activeVersionId}
            scenarioStates={sidebarState.scenarioStates}
            scenarioConditions={sidebarState.scenarioConditions}
            scenarioActiveStateId={sidebarState.scenarioActiveStateId}
            skillsSidebarSections={sidebarState.skillsSidebarSections}
            skillsActiveSkillId={sidebarState.skillsActiveSkillId}
            skillsActiveGroupId={sidebarState.skillsActiveGroupId}
            skillsSidebarRefresh={sidebarState.skillsSidebarRefresh}
            allPrograms={allPrograms}
            onRefreshCourse={() => refreshCourseTree(activeCourseId)}
            onOptimisticUnitsOrder={optimisticUnitsOrder}
            onOptimisticContentOrder={optimisticContentOrder}
            displayLanguage={composerLanguage}
            onSelectLanguage={setComposerLanguage}
            programPageSection={sidebarState.programPageSection}
            lessonSidebarReorder={sidebarState.lessonSidebarReorder}
            onPrefetchTarget={prefetchEditorTarget}
          />
          <div className="pd-shell-route">
            <Suspense fallback={<ContentEditorShellOutletFallback />}>
              <Outlet />
            </Suspense>
          </div>
        </div>
      </div>
      {ErrorPopupComponent}
    </ContentEditorShellProvider>
  );
};

export default ContentEditorShell;
